import { css, keyframes } from 'styled-components';

/**
 * Thea's motion primitives (THE-803, "How Thea moves and responds").
 *
 * Two things live here, and they are the same idea at two altitudes:
 *
 *   1. THE TOKEN SET (THE-810) — the named eases and durations, mirroring the
 *      `--ease-*` / `--dur-*` custom properties in `src/index.css`. Same
 *      relationship `src/theme/index.ts` has to the colour tokens: the CSS
 *      file is where the values live for `var()` consumers, this file is where
 *      they live for anything that cannot resolve a `var()`.
 *   2. THE SHARED BEHAVIOURS (THE-807, THE-808) — `shimmerFill` and
 *      `reducedMotion`, which are whole motions rather than single values.
 *
 * A surface cites the token, not the number: `--dur-sheet`, not 220ms.
 * Anything added here is the single definition of that behaviour — the point
 * of the module is that a surface cannot quietly grow its own dialect, which
 * is how the app ended up with ten shimmers in nine files and two settle
 * curves that differed in one character.
 *
 * WHO NEEDS THE RAW STRINGS RATHER THAN THE VAR. Two kinds of caller, and the
 * distinction is not cosmetic:
 *
 *   - The Web Animations API. `element.animate(frames, { easing })` parses its
 *     easing as a bare <easing-function>, NOT as a CSS value, so a `var()`
 *     there is a syntax error and the animation throws rather than degrading.
 *     `useFlightAnimation` is the one WAAPI caller today; it imports
 *     `EASE_FLIGHT`.
 *   - Anything a test reads back. `BoardProfileNudge`'s transition shorthand is
 *     parsed by `BoardProfileNudge.test.tsx` with a regex over the emitted CSS
 *     to check the per-step stagger, and jsdom does not substitute custom
 *     properties — a `var()` there would make that assertion vacuous rather
 *     than failing. `profileNudgeReveal` therefore re-exports `EASE_SETTLE` as
 *     a literal.
 *
 * Everywhere else, prefer `var(--ease-settle)` in the stylesheet. The sweep in
 * `src/__tests__/motionTokens.test.ts` fails the build on a raw `cubic-bezier`
 * outside this file, which is the point of the exercise: the standard was
 * written because the codebase had shipped two settle curves that differed in
 * one character (`0.22, 1, 0.36, 1` and `0.22, 0.61, 0.36, 1`) and nobody could
 * see the difference by reading a diff.
 */

/* ────────────────────────────── EASES ──────────────────────────────
   Four, and the split is by what the motion MEANS, not by how it looks. A
   fifth curve is almost always one of these four with a different duration. */

/** Something ARRIVES and comes to rest. Sheets, toasts, reveals, drawers. */
export const EASE_SETTLE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** Something LEAVES. Front-loaded, so the element is mostly gone early and the
 *  space it vacated is what the eye follows. Never use it for an entrance. */
export const EASE_EXIT = 'cubic-bezier(0.4, 0, 1, 1)';

/** Celebration — and ONLY save/like. This is the one curve in the set that
 *  overshoots (y1 = 1.56), so it is also the one that reads as "bouncing".
 *  That is a reward for a deliberate user action, not a house entrance style:
 *  THE-802's reveal rules out overshoot by name. */
export const EASE_CELEBRATE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** A → B. An element travelling between two known points (the flight
 *  animation). Symmetric in/out, because the midpoint of the trip is the part
 *  the user is actually tracking. */
export const EASE_FLIGHT = 'cubic-bezier(0.4, 0, 0.2, 1)';

/* ──────────────────────────── DURATIONS ──────────────────────────── */

/** Press/hover feedback on a control. Absorbed the old 120ms — see RETIRED. */
export const DUR_PRESS = '150ms';
/** A small state change in place: a colour, an icon, a chip filling in. */
export const DUR_MICRO = '200ms';
/** A sheet or drawer arriving from an edge. */
export const DUR_SHEET = '220ms';
/** Content revealing itself — THE-802's per-step rise. */
export const DUR_REVEAL = '320ms';
/** A region collapsing or expanding its own height. The ceiling. */
export const DUR_COLLAPSE = '400ms';
/** The board's saved-count tween. Ceiling-exempt. */
export const DUR_COUNTUP = '520ms';
/** One sweep of a skeleton shimmer. Ceiling-exempt (it loops). */
export const DUR_SHIMMER = '1.4s';
/** The 10th-reaction confetti burst (THE-1051). Ceiling-exempt — see
 *  CEILING_EXEMPT_REASONS. Realises the token THE-810 reserved. */
export const DUR_CONFETTI = '1100ms';

/**
 * Name → value, keyed by the CSS custom property. The names are the currency:
 * a PR that changes motion cites `--dur-sheet`, not 220ms.
 *
 * Kept in step with `:root` in `src/index.css` by `motionTokens.test.ts`.
 */
export const MOTION_TOKENS = {
  '--ease-settle': EASE_SETTLE,
  '--ease-exit': EASE_EXIT,
  '--ease-celebrate': EASE_CELEBRATE,
  '--ease-flight': EASE_FLIGHT,
  '--dur-press': DUR_PRESS,
  '--dur-micro': DUR_MICRO,
  '--dur-sheet': DUR_SHEET,
  '--dur-reveal': DUR_REVEAL,
  '--dur-collapse': DUR_COLLAPSE,
  '--dur-countup': DUR_COUNTUP,
  '--dur-shimmer': DUR_SHIMMER,
  '--dur-confetti': DUR_CONFETTI,
} as const;

export type MotionToken = keyof typeof MOTION_TOKENS;

/**
 * The ceiling THE-803 proposes: nothing the user is WAITING ON runs longer
 * than this. Not enforced as a gate yet — the standard proposes the check, and
 * a sweep that failed the build on every duration over 400ms would today flag
 * roughly a dozen legitimate ones. What this file owns is the exemption list,
 * so that when the check does land it already knows what it must not flag.
 */
export const MOTION_CEILING_MS = 400;

/**
 * The durations that are allowed to exceed `MOTION_CEILING_MS`, each with the
 * reason it is exempt. A ceiling with no exemption list is a ceiling that gets
 * deleted the first time it flags a celebration.
 *
 * `--dur-confetti` was RESERVED, not realised, until THE-1051's 10th-reaction
 * celebration gave it a value: the standard named it ahead of the motion so
 * whoever built it would not hit the ceiling and shorten a celebration to
 * satisfy a lint.
 */
export const CEILING_EXEMPT = ['--dur-countup', '--dur-confetti', '--dur-shimmer'] as const;

export const CEILING_EXEMPT_REASONS: Record<(typeof CEILING_EXEMPT)[number], string> = {
  /* 520ms is longer than the 320ms rise it accompanies ON PURPOSE — the number
     has to still be climbing as the tile stops moving, or the two read as two
     unrelated events. See profileNudgeReveal.ts. */
  '--dur-countup': 'The saved count must outlast the tile rise it accompanies.',
  /* Celebration is the one place a user is not waiting on anything: the
     action (the 10th reaction) already succeeded before the burst starts. */
  '--dur-confetti': 'Celebratory, fires after the action already succeeded.',
  /* A loop, not a wait. The ceiling asks "how long until the user can act",
     and a shimmer is the answer to a different question — it is what the user
     looks at WHILE waiting, and a fast sweep reads as a strobe. */
  '--dur-shimmer': 'Loops while content loads; it is not a delay the user waits out.',
};

/**
 * What THE-810 retired, and what replaced it. Kept as data rather than as a
 * changelog entry because `motionTokens.test.ts` sweeps the source for these
 * literals — a retired value that creeps back in fails the build with the
 * reason attached, which is more use to the person who re-adds it than a
 * commit message they will not find.
 */
export const RETIRED_MOTION: ReadonlyArray<{
  value: string;
  replacement: MotionToken | null;
  why: string;
}> = [
  {
    value: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
    replacement: '--ease-settle',
    why: "The duplicate settle curve — one character from --ease-settle, 1 use against its 10. Both are ease-outs that cannot overshoot; the retired one is just slower to arrive.",
  },
  {
    value: 'cubic-bezier(0.32, 0.72, 0, 1)',
    replacement: '--ease-settle',
    why: "vaul's drawer default, inherited with the component. Thea's own sheets already settle on --ease-settle.",
  },
  {
    value: 'cubic-bezier(0.65, 0, 0.35, 1)',
    replacement: null,
    why: 'ResultsCarousel only. Died with the component when THE-811 deleted it as unreachable; it has no replacement because nothing inherited the motion.',
  },
  {
    value: 'cubic-bezier(0.34, 1.15, 0.64, 1)',
    replacement: null,
    why: 'ResultsCarousel only, and a second overshoot curve one control point from --ease-celebrate. Died with the component when THE-811 deleted it as unreachable.',
  },
  {
    value: '120ms',
    replacement: '--dur-press',
    why: 'A second press duration sitting beside 150ms with no rule for picking between them. 30ms is below the threshold at which the difference is perceptible on a press.',
  },
];

/* ───────────────────── SHARED BEHAVIOURS (not values) ───────────────────── */

/**
 * `--dur-shimmer` as a `var()` reference, for use inside an `animation`
 * shorthand.
 *
 * The literal fallback is `DUR_SHIMMER` rather than a second hand-typed `1.4s`
 * — the whole subject of THE-810 is two values that drifted apart because they
 * were typed twice. It keeps the shorthand valid in contexts that mount a
 * component without the global stylesheet (jsdom tests, isolated stories),
 * where an unresolvable `var()` would invalidate the whole declaration and
 * silently drop the animation.
 */
export const DUR_SHIMMER_VAR = `var(--dur-shimmer, ${DUR_SHIMMER})`;

/**
 * The reduced-motion guard, for an animation that is NOT the loading shimmer.
 *
 * Thea's standing decision (THE-803, "Reduced motion"): under
 * `prefers-reduced-motion: reduce` nothing sweeps, pulses or travels. The
 * shimmer already carries this inside `shimmerFill` below, which is the right
 * shape for the loading language — a call site cannot forget it. This export
 * is for the *ambient* animations that are not loading affordances and so have
 * no shared helper to hide in: QuizLoading's breathing logo glyph and its
 * typewriter cursor (THE-807).
 *
 * It is deliberately the same spelling the shimmer uses (`reduce` +
 * `animation: none`) rather than a second dialect — the standard asks for one
 * mechanism, and `shimmerFill`'s own guard is written out inline because it
 * has to target `&::after`, not the element itself.
 *
 * Note what this does NOT belong on: a rotating spinner. Freezing a spinner
 * leaves a static ring that reads as "stuck" — unlike a shimmer, whose
 * shape-matched block survives as a loading affordance, a spinner IS the
 * motion. The board confirmed that reading on 2026-10-05 and chose to leave
 * `BoardFeed`'s and `QuestSummaryPill`'s spinners unguarded; they are named
 * as EXEMPT in `reducedMotionGuardSweep.test.ts` rather than silently skipped.
 */
export const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * A bright band travelling left-to-right across the block it is laid over.
 *
 * `transform` rather than `background-position` on purpose, and not only for
 * the compositor: a `background-position` sweep over `hsl(var(--muted))` does
 * not survive contact with Thea's cream pages. See `shimmerFill`.
 */
const shimmerSweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

/**
 * The loading language, for every surface whose shape we already know.
 *
 * One shape-matched shimmer, sweeping, `--dur-shimmer`, reduced-motion guard
 * included. No spinner — a spinner belongs inside a button you just pressed.
 *
 * WHY A TRAVELLING BAND AND NOT THE OLD `background-position` GRADIENT.
 * The retired shared sweep animated a `hsl(var(--muted) / 0.6 → 0.9 → 0.6)`
 * gradient. `--muted` is `30 10% 95%`, so composited over `creamLight`
 * (`#FAF7F2`) its crest and trough land on `#F4F3F1` and `#F6F4F1` — 1.9/255
 * apart, a contrast ratio of 1.013:1. That is not a sweep the eye can follow;
 * it paints as a flat block, and the block itself sat at 1.026:1 against the
 * page. `HomeSkeleton` reached the same conclusion against live pixels on
 * 2026-09-02 and switched to this band, which runs 1.333:1 band-to-base
 * (36.7/255). Unifying the other way round would have spread the invisible
 * technique across the app and reverted a verified fix, so the band is the
 * shared one. `--dur-shimmer` is unchanged at 1.4s.
 *
 * USING IT.
 * Needs a block box, and owns `position`, `overflow` and `::after` on the
 * element it styles.
 *   - A call site with a deliberate fill colour re-declares `background`
 *     AFTER this helper (`EventCard`'s date-chip peach-clay does).
 *   - A call site that positions itself absolutely re-declares `position`
 *     AFTER this helper (`PreviewCollage` does); an absolute box is still a
 *     containing block for the band.
 */
export const shimmerFill = css`
  position: relative;
  overflow: hidden;
  /* A base light enough to read as "empty", dark enough to be visible on
     cream. Deliberately not hsl(var(--muted)) — see the note above. */
  background: hsl(35 20% 84%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 15%,
      hsl(40 60% 98% / 0.9) 50%,
      transparent 85%
    );
    transform: translateX(-100%);
    animation: ${shimmerSweep} ${DUR_SHIMMER_VAR} ease-in-out infinite;
  }

  /* The guard lives INSIDE the helper so it cannot be forgotten at a call
     site — which is exactly how 8 of 10 shimmers came to be missing it
     (THE-803 finding 2). Reduced motion removes the motion, not the loading
     affordance: the band parks off the left edge under overflow: hidden and
     the shape-matched base block still paints.

     No backticks in this comment on purpose — a backtick inside a styled
     css template literal terminates it. */
  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

/** Shape tokens for the hub's page skeletons. Animation remains owned by
 * shimmerFill (including its reduced-motion guard), never by a page. */
export const pageShimmerShapes = {
  title: css`height: 34px; width: 220px; border-radius: 8px;`,
  search: css`height: 48px; width: 100%; border-radius: 999px;`,
  banner: css`height: 180px; width: 100%; border-radius: 0;`,
  occasion: css`height: 76px; min-width: 240px; border-radius: 18px;`,
  product: css`aspect-ratio: 1; width: 100%; border-radius: 20px;`,
};
